// src/stores/subscriptionStore.js
import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import svc from "@/services/super-subscription.service";
import * as Toast from "@/utils/toast"; // ⬅️ important: import as *

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

export const useSubscriptionStore = defineStore("subscriptions", () => {
  const items = ref([]);
  const meta = ref({ total: 0, per_page: 25, current_page: 1 });
  const loading = ref(false);
  const loadingForCreate = ref(false);

  const stats = reactive({
    total_transactions: 0,
    total_subscribers: 0,
    active_subscribers: 0,
    expired_subscribers: 0,
  });

  const filters = reactive({
    plan_id: "",
    payment_status: "",
    status: "",
    from: "",
    to: "",
    sort_by: "created_at",
    sort_dir: "desc",
  });

  // src/stores/subscriptionStore.js (inside fetchSubscriptions)
  async function fetchSubscriptions(extra = {}) {
    loading.value = true;
    try {
      const params = {
        per_page: 1000,
        page: meta.value.current_page,
        ...filters,
        ...extra,
      };

      const res = await svc.fetch(params);
      // adjust to your actual API shape

      // If API returns { data: [...], meta: {...} }
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];

      items.value = list;
      if (res.meta || res.data?.meta) {
        meta.value = res.meta || res.data.meta;
      }
    } catch (e) {
      console.error(e);
    } finally {
      loading.value = false;
    }
  }

  async function fetchStats() {
    try {
      const res = await svc.stats({
        from: filters.from || undefined,
        to: filters.to || undefined,
        payment_status: filters.payment_status || undefined,
        status: filters.status || undefined,
        plan_id: filters.plan_id || undefined,
      });

      // If svc.stats already returns response.data, this will still work:
      const data = res?.data ?? res;

      stats.total_transactions = Number(data.total_transactions ?? 0);
      stats.total_subscribers = Number(data.total_subscribers ?? 0);
      stats.active_subscribers = Number(data.active_subscribers ?? 0);
      stats.expired_subscribers = Number(data.expired_subscribers ?? 0);
    } catch (e) {
      console.error(e);
      notifyError("Failed to load subscription stats.");
    }
  }

  async function createSubscription(payload) {
    loadingForCreate.value = true;
    try {
      const res = await svc.create(payload);
      notifySuccess("Subscription created successfully.");
      await fetchSubscriptions({ page: 1 });
      await fetchStats();
      return res;
    } catch (e) {
      console.error(e);
      notifyError("Failed to create subscription.");
      throw e;
    } finally {
      loadingForCreate.value = false;
    }
  }

  async function updateSubscription(id, payload) {
    loading.value = true;
    try {
      const res = await svc.update(id, payload);
      notifySuccess("Subscription updated.");
      await fetchSubscriptions();
      await fetchStats();
      return res;
    } catch (e) {
      console.error(e);
      notifyError("Failed to update subscription.");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function removeSubscription(id) {
    loading.value = true;
    try {
      await svc.remove(id);
      notifySuccess("Subscription deleted.");
      await fetchSubscriptions();
      await fetchStats();
    } catch (e) {
      console.error(e);
      notifyError("Failed to delete subscription.");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items,
    meta,
    loading,
    loadingForCreate,
    stats,
    filters,
    fetchSubscriptions,
    fetchStats,
    createSubscription,
    updateSubscription,
    removeSubscription,
  };
});
